import React, { useEffect, useRef } from "react";

export const ALL_COMPETITIONS = "all";

// Chip order — lead with our highest-value leagues so Premier League is always the
// first (and on mobile, the only visible) league chip. Anything not listed falls
// back to A–Z after these. Sorting lives here so every consumer of the filter
// (dashboard fixtures, team list, freemium teaser) orders leagues identically.
export const COMPETITION_PRIORITY = ["Premier League", "La Liga", "Major League Soccer"];

// Pill tab look shared by the league filter and the dashboard's day tabs
// (Past / Yesterday / Today / …): light active pill, outlined inactive ones.
export const pillTabClass = (isActive) =>
    `border rounded-4 px-3 py-md-25 py-2 small fw-semi-bold flex-shrink-0 text-nowrap ${isActive
        ? "bg-neutral-gray-100 border-neutral-gray-100 text-dark-mode-900"
        : "bg-transparent border-dark-mode-100 text-neutral-gray-700"}`;

export const sortCompetitions =(competitions = []) => {
    const rank = (name) => {
        const i = COMPETITION_PRIORITY.indexOf(name);
        return i === -1 ? COMPETITION_PRIORITY.length : i;
    };
    return [...competitions].sort((a, b) =>
        rank(a.season_name) - rank(b.season_name) ||
        (a.season_name || "").localeCompare(b.season_name || "")
    );
};

// Shorter tab labels. Display only; ids are untouched.
const TAB_LABELS = { "Major League Soccer": "MLS" };

// Pill tabs: light active pill, outlined inactive ones. Used for every league filter.
// `showAll` opts out of the leading "All" tab, for places that always want exactly
// one league selected (e.g. the landing hero, where the cards below are per-league).
// `leadingOptions` ({ id, label }) go before the leagues, e.g. the dashboard's "For you".
// `id` lets a page that renders more than one filter keep its ids unique.
const CompetitionFilter = ({ competitions, value, onChange, showAll = true, leadingOptions = [], id = "competition-filter" }) => {
    const options = [
        ...leadingOptions,
        ...(showAll && competitions.length > 1 ? [{ id: ALL_COMPETITIONS, label: "All" }] : []),
        ...sortCompetitions(competitions).map((comp) => ({
            id: String(comp.id),
            label: TAB_LABELS[comp.season_name] || comp.season_name,
        })),
    ];

    // Keep the active chip centred in the rail, like the dashboard's tabs — e.g. when
    // the default lands on a league at the far end. Scrolls the rail itself rather
    // than using scrollIntoView, which would also scroll the page to the filter.
    const railRef = useRef(null);
    const activeIndex = options.findIndex((option) => String(value) === option.id);

    useEffect(() => {
        const rail = railRef.current;
        const chip = rail?.children[activeIndex];
        if (!chip || rail.scrollWidth <= rail.clientWidth) return;

        const railRect = rail.getBoundingClientRect();
        const chipRect = chip.getBoundingClientRect();
        const left = rail.scrollLeft + (chipRect.left - railRect.left) - (rail.clientWidth - chipRect.width) / 2;
        rail.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    }, [activeIndex, options.length]);

    return (
        <div
            ref={railRef}
            id={id}
            className={`d-flex column-gap-2 overflow-x-auto flex-nowrap pb-2`}
            style={{ WebkitOverflowScrolling: "touch" }}
        >
            {options.map((option) => {
                const isActive = String(value) === option.id;
                return (
                    <button
                        key={option.id}
                        type="button"
                        onClick={() => onChange(option.id)}
                        aria-pressed={isActive}
                        className={pillTabClass(isActive)}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default CompetitionFilter;
